import { type ChangeEvent, useEffect, useState } from 'react'
import { Form, useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import { CompanyNameInput, VatNumberInput } from '@dhl/inputs'
import { CheckboxInput, TextFieldInput, SelectInput } from '@dhl/formik'
import { AddressInput } from '@dhl/input-address'
import { useGetBusinessUnit } from '@dhl/mdp-api'
import { Box, Button, Checkbox, FormGroup, Grid, H6, MenuItem } from '@dhl-ui/components'
import type { FormType } from '~/types'

import { useDataAsyncCall } from '~/api/admin-api'
import type { Organisation } from '~/modules/organisations/model'
import { useBillingUnits } from '~/modules/billingunits/hooks'
import { CountrySelect } from '~/common/components/addresses'
import { isBeNlBusinessUnit, isCccBusinessUnit, isPplCzBusinessUnit } from '~/common/model/businessunits'
import { config } from '~/config'

type Props = {
  country: string
  type: FormType
  organisation: Organisation
  onCancel: () => void
  onChangeCountry: (value: string) => void
}

type ResponsePossibleBusinessUnit = {
  businessUnitCodes: string[]
}

export const OrganisationForm = ({ country, type, organisation, onCancel, onChangeCountry }: Props) => {
  const { t } = useTranslation()
  const { disableCccOrgCrud } = config
  const [sharedShipments, setSharedShipments] = useState(organisation.useSharedShipments)
  const { setFieldValue } = useFormikContext<Organisation>()
  const disableCustomerId = !!organisation.ccc?.customerId && type === 'edit'

  const { billingUnits } = useBillingUnits()

  const { data: allBusinessUnits } = useGetBusinessUnit()

  const [possibleBusinessUnitsResponse] = useDataAsyncCall<ResponsePossibleBusinessUnit>(
    `admin-user/possible-business-unit-codes`
  )
  const possibleBusinessUnitsCodes = possibleBusinessUnitsResponse?.businessUnitCodes

  const possibleBusinessUnits = allBusinessUnits?.filter((item: { code: string }) => {
    return possibleBusinessUnitsCodes?.some((value: string) => {
      return item.code === value
    })
  })

  possibleBusinessUnits?.sort((preIndex, postIndex) => {
    if (preIndex.name.toLocaleLowerCase() < postIndex.name.toLocaleLowerCase()) {
      return -1
    }
    if (preIndex.name.toLocaleLowerCase() > postIndex.name.toLocaleLowerCase()) {
      return 1
    }
    return 0
  })

  const isCCC = isCccBusinessUnit(organisation.businessUnitCode)
  const isPplCz = isPplCzBusinessUnit(organisation.businessUnitCode)
  const showLabelSettings = isBeNlBusinessUnit(organisation.businessUnitCode)
  const showBillingUnitField = isCCC && billingUnits.length > 0
  const disableFields = disableCccOrgCrud && type === 'edit' && isCCC

  // Auto-select business unit when only one is available
  useEffect(() => {
    if (possibleBusinessUnits?.length === 1 && !organisation.businessUnitCode) {
      setFieldValue('businessUnitCode', possibleBusinessUnits[0].code)
    }
  }, [possibleBusinessUnits, organisation.businessUnitCode, setFieldValue])

  // Auto-select country to CZ when PPL-CZ is selected
  useEffect(() => {
    if (isPplCz && organisation.data.address.countryCode !== 'CZ') {
      setFieldValue('data.address.countryCode', 'CZ')
      onChangeCountry('CZ')
    }
  }, [isPplCz, organisation.data.address.countryCode, onChangeCountry, setFieldValue])

  if (!allBusinessUnits && !possibleBusinessUnitsCodes) return null

  const onChangeSharedShipments = async (event: ChangeEvent<HTMLInputElement>) => {
    setSharedShipments(event.target.checked)
    await setFieldValue('useSharedShipments', event.target.checked)
    if (!event.target.checked) {
      await setFieldValue('useSharedTickets', false)
      await setFieldValue('useSharedDrafts', false)
    }
  }

  const onChangeSharedTickets = (event: ChangeEvent<HTMLInputElement>) => {
    setFieldValue('useSharedTickets', event.target.checked)
  }

  const onChangeSharedDrafts = (event: ChangeEvent<HTMLInputElement>) => {
    setFieldValue('useSharedDrafts', event.target.checked)
  }

  return (
    <Form data-testid="organisation-form">
      <Box pt={1} pb={1}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <H6 data-testid={'acccount'}>{t('admin.organisationsPage.organisationForm.accountInformation')}</H6>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <SelectInput name="businessUnitCode" fullWidth label={t('admin.accountManagers.businessUnitCode')}>
              {(possibleBusinessUnits ?? []).map(({ code, name }) => (
                <MenuItem
                  disabled={type === 'edit' || (type === 'add' && disableCccOrgCrud && isCccBusinessUnit(code))}
                  key={code}
                  value={code}
                >
                  {name}
                </MenuItem>
              ))}
            </SelectInput>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <CompanyNameInput disabled={disableFields} name="name" fullWidth />
          </Grid>
          {isPplCz ? (
            <Grid size={{ xs: 6 }}>
              <TextFieldInput
                disabled={disableFields}
                fullWidth
                label={t('admin.organisationsPage.organisationForm.icoNumber')}
                name="data.icoNumber"
                type="text"
                data-testid="Ico"
              />
            </Grid>
          ) : (
            <Grid size={{ xs: 6 }}>
              <TextFieldInput
                disabled={disableFields}
                fullWidth
                label={t('admin.organisationsPage.organisationForm.COCNumber')}
                name="data.chamberOfCommerce"
                type="text"
                data-testid="Coc"
              />
            </Grid>
          )}
          <Grid size={{ xs: 6 }}>
            <VatNumberInput disabled={disableFields} name="data.vatNumber" data-testid="vatNumber" fullWidth />
          </Grid>
          {showBillingUnitField && (
            <>
              <Grid size={{ xs: 12 }}>
                <H6>{t('admin.organisationsPage.organisationForm.CCCCustomerInformation')}</H6>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextFieldInput
                  fullWidth
                  disabled={disableCustomerId || disableFields}
                  label={t('admin.organisationsPage.organisationForm.customerID')}
                  name="ccc.customerId"
                  type="text"
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <SelectInput
                  label={t('admin.billingUnitsPage.billingUnitsOverview.identifier')}
                  name="ccc.billingUnitId"
                  fullWidth
                >
                  {billingUnits.map((billingUnit) => (
                    <MenuItem key={billingUnit.id} value={billingUnit.id} disabled={disableFields}>
                      {billingUnit.name}
                    </MenuItem>
                  ))}
                </SelectInput>
              </Grid>
            </>
          )}
          <Grid size={{ xs: 12 }}>
            <H6>{t('admin.organisationsPage.organisationForm.addressInformation')}</H6>
          </Grid>
          <Grid>
            <AddressInput disabled={disableFields} carrier="parcel" country={country} name="data.address" />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <CountrySelect
              disabled={disableFields}
              fieldName="data.address.countryCode"
              onChangeCountryCode={onChangeCountry}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <H6>{t('admin.organisationsPage.organisationForm.contact')}</H6>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextFieldInput
              disabled={disableFields}
              fullWidth
              label={t('admin.organisationsPage.organisationForm.firstName')}
              name="data.contactPerson.firstName"
              type="text"
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextFieldInput
              disabled={disableFields}
              fullWidth
              label={t('admin.organisationsPage.organisationForm.lastName')}
              name="data.contactPerson.lastName"
              type="text"
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextFieldInput
              disabled={disableFields}
              fullWidth
              label={t('admin.organisationsPage.organisationForm.emailAddress')}
              name="data.contactPerson.email"
              type="text"
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextFieldInput
              disabled={disableFields}
              fullWidth
              label={t('admin.organisationsPage.organisationForm.phone')}
              name="data.contactPerson.phone"
              type="text"
            />
          </Grid>
          {showLabelSettings && (
            <>
              <Grid size={{ xs: 12 }}>
                <H6>{t('admin.organisationsPage.organisationForm.labelSettings')}</H6>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextFieldInput
                  disabled={disableFields}
                  fullWidth
                  label={t('admin.organisationsPage.organisationForm.postboxNumber')}
                  name="data.labelSettings.undeliverableReturnPostboxNumber"
                  type="text"
                  data-testid="postboxNumber"
                />
              </Grid>
            </>
          )}
          <Grid size={{ xs: 12 }}>
            <H6>{t('admin.organisationsPage.organisationForm.otherOptions')}</H6>
          </Grid>
          <FormGroup>
            <Box
              sx={{
                mt: 2,
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gridTemplateRows: 'repeat(4, auto)',
                gap: 0.5,
              }}
            >
              <CheckboxInput
                name="useCompanyAddressBook"
                label={t('admin.organisationsPage.organisationForm.companyAddressBook').toString()}
              />

              <Checkbox
                name="useSharedShipments"
                checked={organisation.useSharedShipments}
                onChange={onChangeSharedShipments}
                label={t('admin.organisationsPage.organisationForm.sharedShipments').toString()}
              />
              <CheckboxInput
                name="useAdvancedParcelType"
                label={t('admin.organisationsPage.organisationForm.specifyWeight').toString()}
              />

              <Checkbox
                name="useSharedTickets"
                checked={organisation.useSharedTickets}
                onChange={onChangeSharedTickets}
                disabled={!sharedShipments}
                label={t('admin.organisationsPage.organisationForm.sharedTickets').toString()}
              />
              <CheckboxInput
                name="useSharedImportSchemas"
                label={t('admin.organisationsPage.organisationForm.sharedImportSchemas').toString()}
              />
              <CheckboxInput
                name="useSharedDrafts"
                checked={organisation.useSharedDrafts}
                onChange={onChangeSharedDrafts}
                disabled={!sharedShipments}
                label={t('admin.organisationsPage.organisationForm.sharedDrafts').toString()}
              />
            </Box>
          </FormGroup>
        </Grid>
      </Box>
      <Box pt={1} display="flex" justifyContent="flex-end" gap={1}>
        <Button color="primary" variant="text" size="large" onClick={onCancel}>
          {t('admin.organisationsPage.organisationForm.cancel')}
        </Button>
        <Button color="primary" type="submit" variant="contained" size="large">
          {t(`admin.organisationsPage.organisationForm.${type === 'add' ? 'addOrganisation' : 'updateOrganisation'}`)}
        </Button>
      </Box>
    </Form>
  )
}
